import { AlertTriangle, BatteryCharging, Zap } from "lucide-react";
import { Card } from "../Card.js";
import { cn } from "../../lib/cn.js";
// Imported rather than referenced as `/solar_roof.jpg`. Vite copies `public/`
// to the root of `dist`, and `apps/api/src/routes/web.ts` serves only `/` and
// `/assets/*` -- so a public-root image 404s in production while working
// perfectly in dev. Importing it makes Vite hash it into `dist/assets/`.
import roof from "../../assets/images/solar-roof.jpg";

interface Props {
  siteName: string;
  /** Charging, discharging or idle, from the sign of battery power. */
  mode: string;
  /** Faults and warnings the inverter is currently reporting. */
  alerts: number;
  onShowAlerts: () => void;
  className?: string;
}

/**
 * The dashboard's opening panel: the photograph, the greeting, the mode chips.
 *
 * **The two round buttons are indicators, not controls, and that is not a
 * shortcut.** In the design they set an inverter power mode. This system does
 * not write to the inverter and must not: `DECISIONS/0004` rules out mode
 * switching, charge-current changes and register writes altogether, and the
 * collector actively blocks configure records travelling towards the hardware
 * -- there is a log line in production proving it refused one. A button that
 * looked like it changed the inverter's mode and silently did nothing would be
 * worse than no button, so they show which mode the inverter reports and are
 * not operable.
 *
 * The greeting takes the configured site name. The design hardcodes its own
 * brand there, and this instance is a product other SPF owners can run.
 */
export function HeroCard({ siteName, mode, alerts, onShowAlerts, className }: Props) {
  return (
    <Card
      title=""
      className={cn(
        "group relative col-span-1 flex min-h-[340px] flex-col justify-between overflow-hidden",
        "border-neutral-800 bg-neutral-900 p-6 text-white sm:min-h-[380px] sm:p-8",
        "md:col-span-2 lg:row-span-2 lg:min-h-[440px]",
        className,
      )}
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
        <img
          src={roof}
          alt=""
          className="h-full w-full object-cover object-center opacity-30 transition-transform duration-1000 ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060B08] via-[#060B08]/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#060B08] via-[#060B08]/50 to-transparent" />
        <div className="absolute -left-24 -top-24 h-96 w-96 rounded-full bg-[#25C16F]/20 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-md pt-2">
        <p className="mb-4 text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-[54px]">
          Welcome in,
          <br />
          {siteName}.
        </p>
        <p className="text-base font-normal leading-relaxed text-neutral-300 sm:text-lg">
          Monitor your power. Understand the cost. Keep the lights on.
        </p>
      </div>

      <div className="relative z-10 flex items-center justify-between pt-8">
        <div className="flex items-center gap-3">
          <ModeChip
            label="Charging"
            active={mode === "Charging"}
            icon={<BatteryCharging size={20} />}
          />
          <ModeChip label="Discharging" active={mode === "Discharging"} icon={<Zap size={20} />} />
          <button
            type="button"
            onClick={onShowAlerts}
            className={cn(
              "relative flex h-12 w-12 items-center justify-center rounded-full border backdrop-blur-md transition-all duration-200",
              alerts > 0
                ? "border-red-500/30 bg-red-500/20 text-red-300 hover:bg-red-500/30"
                : "border-white/10 bg-white/10 text-white hover:bg-white/20",
            )}
          >
            <span className="sr-only">
              {alerts > 0 ? `${alerts} inverter alerts` : "Inverter alerts, none reported"}
            </span>
            <AlertTriangle size={20} aria-hidden="true" />
            {alerts > 0 && (
              <span
                aria-hidden="true"
                className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white ring-2 ring-neutral-900"
              >
                {alerts}
              </span>
            )}
          </button>
        </div>

        <p className="hidden items-center gap-2 rounded-full border border-white/10 bg-black/40 px-3 py-1.5 text-xs text-neutral-300 backdrop-blur-md sm:flex">
          {/* Not the only carrier: the word beside it says the same thing, so a
              reader who cannot see the dot loses nothing. */}
          <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[#25C16F]" />
          <span className="tabular">{mode}</span>
        </p>
      </div>
    </Card>
  );
}

/**
 * A state the inverter is in, drawn as the design's action button. Rendered as
 * a `<p>` rather than a `<button>` on purpose -- see the note above about
 * write-control.
 */
function ModeChip({
  label,
  active,
  icon,
}: {
  label: string;
  active: boolean;
  icon: React.ReactNode;
}) {
  return (
    <p
      className={cn(
        "flex h-12 w-12 items-center justify-center rounded-full border backdrop-blur-md transition-all duration-200",
        active
          ? "scale-105 border-[#E5F145] bg-[#E5F145] text-black shadow-lg shadow-[#E5F145]/20"
          : "border-white/10 bg-white/10 text-white",
      )}
    >
      <span className="sr-only">{active ? `${label}: yes` : `${label}: no`}</span>
      <span aria-hidden="true">{icon}</span>
    </p>
  );
}
